import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import CaseIcon from '../../assets/icons/CaseIcon';
import ExportIcon from '../../assets/icons/DownloadIcon';
import FilterIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';

import CasesTableContainer from '../cases/CasesTableContainer';
import ActionButton from '../shared/ActionButton';

import { exportCases, getSetsAllocation } from '../../actions/casesActions';

import { formatDate } from '../../utils/date';
import { formatPrice } from '../../utils/utils';

import routes from '../../constants/routes';
import { kitVariantTypes, setAllocationStatuses } from '../../constants/enums';

import './reporting.scss';

const getCaseRouteId = (caseItem) => caseItem?.caseId || caseItem?.docId || caseItem?.id || '';

const ReportingCasesTable = (props) => {
  const {
    cases, surgeons, hospitals, procedures, users,
    filter, filterOptions, onFilterChange, onRemoveFilter,
  } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const [exportLoading, setExportLoading] = useState(false);

  const getName = (field, id) => {
    let list = [];

    if (field === 'surgeonName') {
      list = surgeons;
    } else if (field === 'hospitalName') {
      list = hospitals;
    } else if (field === 'procedureName') {
      list = procedures;
    }

    const value = list?.find((item) => item.id === id);

    if (value) {
      return field === 'surgeonName' ? `${value?.firstName} ${value?.lastName}` : value.name;
    }

    return '';
  };

  const getProcedureValue = useCallback((procedureId) => {
    const procedure = procedures.find((p) => p.id === procedureId);
    return procedure.averageValue || 0;
  }, [procedures]);

  const getHospitalState = useCallback((hospitalId) => {
    const hospital = hospitals?.find((hospital) => hospital.id === hospitalId);
    return hospital ? hospital.state : '';
  }, [hospitals]);

  const getUserName = useCallback((uid) => {
    const user = users?.find((u) => u.uid === uid);
    return user ? `${user.firstName} ${user.lastName}` : '';
  }, [users]);

  const getKitVariant = (caseItem) => {
    if (caseItem.kitVariant === kitVariantTypes.consignment) {
      if (caseItem.additionalKits?.length || caseItem.additionalItems?.length) {
        return 'LOAN + CONSIGNMENT';
      } else {
        return 'CONSIGNMENT';
      }
    } else {
      return 'LOAN';
    }
  };

  const getReturnDate = useCallback((allocations) => {
    const dates = allocations?.filter((item) => !!item.returnDate).map((item) => formatDate(item.returnDate));
    const uniq = Array.from(new Set(dates));
    return uniq.join(', ');
  }, [cases]);

  const onExportClick = async () => {
    setExportLoading(true);

    let _cases = [...cases];
    const concurrencyLimit = 200;
    for (let i = 0; i < _cases.length; i += concurrencyLimit) {
      const batch = _cases.slice(i, i + concurrencyLimit);
      await Promise.all(
        batch.map(async (caseItem) => {
          const setsAllocation = await dispatch(getSetsAllocation(getCaseRouteId(caseItem), true));
          if (setsAllocation?.length) {
            caseItem.setsAllocation = setsAllocation;
          }
        })
      );
    }

    const data = _cases?.map((item) => ({
      id: getCaseRouteId(item),
      surgeon: getName('surgeonName', item.surgeon),
      hospital: getName('hospitalName', item.hospital),
      hospitalState: getHospitalState(item.hospital),
      procedure: getName('procedureName', item.procedure),
      createdBy: getUserName(item.createdBy),
      patientReference: item.patientReference || '',
      date: item.date,
      createdAt: item.createdAt,
      status: item.status,
      kitVariant: getKitVariant(item),
      collectionDate: item.collectionDate || null,
      procedureValue: `$${formatPrice(getProcedureValue(item.procedure), 0)}`,
      returnDate: getReturnDate(item.setsAllocation),
    }));
    await dispatch(exportCases(data));
    setExportLoading(false);
  };

  return (
    <div className='cases-table__container'>
      <div className='d-flex m-b-lg space-between align-start'>
        <div className='d-flex'>
          <CaseIcon color="var(--app-text)" />
          <div className='font-size-bg font-bold m-l-md'>
            Cases
          </div>
        </div>

        <ActionButton
          onClick={onExportClick}
          bgColor={setAllocationStatuses.SHIPPED.color}
          disabled={exportLoading}
          loading={exportLoading}
        >
          <ExportIcon color="#ffffff" />
          Export
        </ActionButton>
      </div>

      {filter && Object.keys(filter).some((key) => filter[key].length) && (
        <div className='filters-list__container'>
          <FilterIcon style={{ color: 'var(--app-text)' }} />

          {Object.keys(filter).map((key) => (
            <div key={key} className='d-flex'>
              {filter[key].map((value) => (
                <div key={`${key}-${value}`} className='filters-list__item'>
                  <div />
                  <div className='filter-list-item-value'>{getName(key, value)}</div>
                  <CloseIcon color='secondary' onClick={() => onRemoveFilter(key, value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <CasesTableContainer
        cases={cases}
        surgeons={surgeons}
        hospitals={hospitals}
        procedures={procedures}
        users={users}
        withoutCheck
        rowColor='#ffffff'
        rowBorderColor='#fafafa'
        onSelectCase={(item) => history.push(`${routes.CASES}/${getCaseRouteId(item)}`)}
        checkboxes={false}
        pagination
        isReport
        filter={filter}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
      />
    </div>
  );
};

export default ReportingCasesTable;
